import React, {useState, useEffect, useCallback} from 'react';
import {
    Box, Stack, Select, MenuItem, FormControl, InputLabel, TextField,
    IconButton, Button, ToggleButtonGroup, ToggleButton, Alert,
    Autocomplete, Typography, FormHelperText, Avatar, ListItemIcon, ListItemText,
    FormControlLabel, Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CodeIcon from '@mui/icons-material/Code';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TokenIcon from '@mui/icons-material/Token';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import {Coins} from 'lucide-react';
import {JsonEditor as JsonEditReact} from 'json-edit-react';
import apiService from '../services/apiService';

const ANY_IDENTIFIER = '__any__';

// Mirrors ChallengeCriteriaObject.validateByRules (common-lib) exactly — the entity
// picker only ever offers what the backend will actually accept for that action.
const ACTION_CONFIG = {
    game_play: {label: 'Play Game', entities: ['machine_category'], allowAny: true},
    game_win: {label: 'Win Game', entities: ['machine_category'], allowAny: true, showInARow: true},
    register: {label: 'Register', entities: ['player'], noItemPicker: true, fixedAmount: 1},
    complete: {label: 'Complete Challenge', entities: ['challenge']},
    increase: {label: 'Earn (Increase Balance)', entities: ['reward', 'currency', 'gelt']},
    decrease: {label: 'Spend (Decrease Balance)', entities: ['reward', 'currency', 'gelt']},
};

const ALL_ACTIONS = Object.entries(ACTION_CONFIG).map(([value, cfg]) => ({value, label: cfg.label}));

const ENTITY_META = {
    machine_category: {label: 'Machine Category', icon: <CategoryIcon fontSize="small"/>},
    player: {label: 'Player', icon: <PersonIcon fontSize="small"/>},
    challenge: {label: 'Challenge', icon: <EmojiEventsIcon fontSize="small"/>},
    reward: {label: 'Reward', icon: <CardGiftcardIcon fontSize="small"/>},
    currency: {label: 'Currency', icon: <Coins size={18}/>},
    gelt: {label: 'Gelt', icon: <TokenIcon fontSize="small"/>},
};

const entitiesForAction = (action) => (ACTION_CONFIG[action] || ACTION_CONFIG.game_play).entities;

const validateRow = (item) => {
    const errors = {};
    const config = ACTION_CONFIG[item.action];
    if (!item.action) errors.action = 'Required';
    if (!item.entity) errors.entity = 'Required';
    if (config && item.entity && !config.entities.includes(item.entity)) {
        errors.entity = `Must be ${config.entities.join(' or ')} for this action`;
    }
    if (config && !config.noItemPicker && !config.allowAny && !item.criteria?.identifier) {
        errors.identifier = 'Select an item';
    }
    if (!(config && config.fixedAmount)) {
        const amount = item.criteria?.amount;
        if (amount === undefined || amount === null || amount === '') {
            errors.amount = 'Required';
        } else if (typeof amount === 'number' && amount <= 0) {
            errors.amount = 'Must be > 0';
        }
    }
    return errors;
};

const canSwitchToVisual = (value) => {
    return Array.isArray(value) && (value.length === 0 || value.every(item =>
        item?.action && item?.entity && ACTION_CONFIG[item.action]
    ));
};

function ChallengeCriteriaEditor({value = [], onChange, disabled = false, showErrors = false, excludeChallengeId = null}) {
    const [mode, setMode] = useState('visual');
    const [textValue, setTextValue] = useState(() => JSON.stringify(value, null, 2));
    const [textError, setTextError] = useState(null);
    const [entityData, setEntityData] = useState({machine_category: [], reward: [], currency: [], gelt: [], challenge: []});
    const [loadingData, setLoadingData] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [switchError, setSwitchError] = useState(null);

    useEffect(() => {
        const failed = [];
        const safeFetch = (url, label) => apiService.get(url).catch(() => { failed.push(label); return []; });
        Promise.all([
            safeFetch('/api/categories', 'categories'),
            safeFetch('/api/rewards', 'rewards'),
            safeFetch('/api/currencies', 'currencies'),
            safeFetch('/api/gelts', 'gelts'),
            safeFetch('/api/challenges', 'challenges'),
        ]).then(([categories, rewards, currencies, gelts, challenges]) => {
            setEntityData({
                machine_category: categories,
                reward: rewards,
                currency: currencies,
                gelt: gelts,
                challenge: challenges.filter(c => c.id !== excludeChallengeId),
            });
            // Distinguish "genuinely empty list" from "failed to load" so a picker
            // that looks empty isn't mistaken for "there's nothing to select here".
            setFetchError(failed.length ? `Failed to load: ${failed.join(', ')}` : null);
        }).finally(() => setLoadingData(false));
    }, [excludeChallengeId]);

    const handleModeChange = useCallback((_, newMode) => {
        if (!newMode) return;
        setSwitchError(null);

        if (mode === 'text') {
            try {
                const parsed = JSON.parse(textValue);
                if (newMode === 'visual' && !canSwitchToVisual(parsed)) {
                    setSwitchError('Cannot switch to Visual: every item needs a recognized action');
                    return;
                }
                onChange(parsed);
                setTextError(null);
            } catch {
                setSwitchError('Fix JSON errors before switching');
                return;
            }
        } else if (newMode === 'visual' && !canSwitchToVisual(value)) {
            setSwitchError('Cannot switch to Visual: every item needs a recognized action');
            return;
        }

        if (newMode === 'text') {
            setTextValue(JSON.stringify(value, null, 2));
            setTextError(null);
        }
        setMode(newMode);
    }, [value, textValue, mode, onChange]);

    const handleTextChange = useCallback((e) => {
        const raw = e.target.value;
        setTextValue(raw);
        try {
            const parsed = JSON.parse(raw);
            setTextError(null);
            onChange(parsed);
        } catch {
            setTextError('Invalid JSON');
        }
    }, [onChange]);

    const updateRow = (index, field, val) => {
        const updated = [...value];
        if (field === 'action') {
            const config = ACTION_CONFIG[val];
            const currentEntity = updated[index].entity;
            const entityChanged = !config.entities.includes(currentEntity);
            const entity = entityChanged ? config.entities[0] : currentEntity;
            // An identifier only means something relative to the entity it was picked
            // against — if the entity changed (or there's no picker at all), a leftover
            // identifier would silently be reinterpreted as an id of a different entity
            // type. Only entity-preserving action switches (e.g. increase <-> decrease,
            // both reward/currency/gelt) may keep it.
            const nextIdentifier = (config.noItemPicker || entityChanged)
                ? ''
                : (updated[index].criteria?.identifier ?? '');
            const nextCriteria = config.fixedAmount
                ? {amount: config.fixedAmount}
                : {...updated[index].criteria, identifier: nextIdentifier};
            if (!config.showInARow) delete nextCriteria.in_a_row;
            updated[index] = {...updated[index], action: val, entity, criteria: nextCriteria};
        } else if (field === 'entity') {
            updated[index] = {...updated[index], entity: val, criteria: {...updated[index].criteria, identifier: ''}};
        } else if (field === 'identifier') {
            updated[index] = {...updated[index], criteria: {...updated[index].criteria, identifier: val === ANY_IDENTIFIER ? '' : val}};
        } else if (field === 'amount') {
            updated[index] = {...updated[index], criteria: {...updated[index].criteria, amount: val}};
        } else if (field === 'in_a_row') {
            updated[index] = {...updated[index], criteria: {...updated[index].criteria, in_a_row: val || undefined}};
        }
        onChange(updated);
    };

    const addRow = () => onChange([...value, {action: 'game_play', entity: 'machine_category', criteria: {identifier: '', amount: 1}}]);

    const removeRow = (index) => onChange(value.filter((_, i) => i !== index));

    const getItemOptions = (entityType) => {
        return (entityData[entityType] || []).map(item => ({
            identifier: String(item.id),
            label: item.name || item.identifier || String(item.id),
            iconUrl: item.iconUrl || null,
        }));
    };

    const renderVisual = () => (
        <Stack spacing={1.5}>
            {value.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{py: 1}}>
                    No entries. Click "Add Entry" to begin.
                </Typography>
            )}
            {value.map((item, index) => {
                const config = ACTION_CONFIG[item.action] || ACTION_CONFIG.game_play;
                const entityOptions = entitiesForAction(item.action);
                const options = config.noItemPicker ? [] : getItemOptions(item.entity);
                const itemId = String(item.criteria?.identifier ?? '');
                const selectedOption = itemId === '' ? null : (options.find(o => o.identifier === itemId) || null);
                const errors = showErrors ? validateRow(item) : {};

                return (
                    <Box key={index} sx={{
                        p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                        display: 'flex', flexDirection: 'column', gap: 1.25,
                    }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{flexWrap: 'wrap', rowGap: 1}}>
                            <FormControl size="small" sx={{minWidth: 170, flexShrink: 0}} error={!!errors.action}>
                                <InputLabel>Action</InputLabel>
                                <Select
                                    value={item.action || 'game_play'}
                                    label="Action"
                                    onChange={e => updateRow(index, 'action', e.target.value)}
                                    disabled={disabled}
                                >
                                    {ALL_ACTIONS.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                                </Select>
                                {errors.action && <FormHelperText>{errors.action}</FormHelperText>}
                            </FormControl>

                            <FormControl size="small" sx={{minWidth: 150, flexShrink: 0}} error={!!errors.entity}>
                                <InputLabel>Entity</InputLabel>
                                <Select
                                    value={item.entity || entityOptions[0]}
                                    label="Entity"
                                    onChange={e => updateRow(index, 'entity', e.target.value)}
                                    disabled={disabled || entityOptions.length === 1}
                                    renderValue={val => {
                                        const meta = ENTITY_META[val];
                                        return meta ? (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                {meta.icon}<span>{meta.label}</span>
                                            </Stack>
                                        ) : val;
                                    }}
                                >
                                    {entityOptions.map(e => (
                                        <MenuItem key={e} value={e}>
                                            <ListItemIcon sx={{minWidth: 28}}>{ENTITY_META[e].icon}</ListItemIcon>
                                            <ListItemText>{ENTITY_META[e].label}</ListItemText>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.entity && <FormHelperText>{errors.entity}</FormHelperText>}
                            </FormControl>

                            {config.noItemPicker ? (
                                <Typography variant="body2" color="text.secondary" sx={{alignSelf: 'center', flex: 1}}>
                                    Fires once, automatically, on registration.
                                </Typography>
                            ) : (
                                <Autocomplete
                                    size="small"
                                    sx={{minWidth: 180, flex: 1}}
                                    options={config.allowAny ? [{identifier: ANY_IDENTIFIER, label: 'Any', iconUrl: null}, ...options] : options}
                                    getOptionLabel={o => o.label}
                                    value={selectedOption || (config.allowAny ? {identifier: ANY_IDENTIFIER, label: 'Any', iconUrl: null} : null)}
                                    onChange={(_, newVal) => updateRow(index, 'identifier', newVal?.identifier || '')}
                                    isOptionEqualToValue={(option, val) => option.identifier === val?.identifier}
                                    disabled={disabled}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.identifier}>
                                            {option.iconUrl && <Avatar src={option.iconUrl} sx={{width: 20, height: 20, mr: 1}}/>}
                                            {option.label}
                                        </li>
                                    )}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={item.entity === 'challenge' ? 'Challenge' : 'Item'}
                                            error={!!errors.identifier}
                                            helperText={errors.identifier}
                                        />
                                    )}
                                />
                            )}

                            <IconButton color="error" onClick={() => removeRow(index)} disabled={disabled} size="small"
                                        sx={{mt: '4px', flexShrink: 0}} aria-label="Remove entry">
                                <DeleteIcon/>
                            </IconButton>
                        </Stack>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                            {config.fixedAmount ? (
                                <Typography variant="caption" color="text.secondary">Amount: always 1</Typography>
                            ) : (
                                <TextField
                                    size="small" type="number" label="Amount"
                                    sx={{width: 140, flexShrink: 0}}
                                    value={item.criteria?.amount ?? ''}
                                    onChange={e => updateRow(index, 'amount', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    disabled={disabled}
                                    slotProps={{htmlInput: {min: 1}}}
                                    error={!!errors.amount}
                                    helperText={errors.amount}
                                />
                            )}
                            {config.showInARow && (
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={!!item.criteria?.in_a_row}
                                                       onChange={e => updateRow(index, 'in_a_row', e.target.checked)}
                                                       disabled={disabled}/>}
                                    label="In a row (streak resets on a loss)"
                                    sx={{m: 0, '& .MuiFormControlLabel-label': {fontSize: '0.85rem'}}}
                                />
                            )}
                        </Stack>
                    </Box>
                );
            })}
            <Box>
                <Button startIcon={<AddIcon/>} onClick={addRow} disabled={disabled} size="small">Add Entry</Button>
            </Box>
        </Stack>
    );

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}}>
                <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                    <ToggleButton value="visual"><ViewListIcon sx={{mr: 0.5}}/> Visual</ToggleButton>
                    <ToggleButton value="tree"><AccountTreeIcon sx={{mr: 0.5}}/> Tree</ToggleButton>
                    <ToggleButton value="text"><CodeIcon sx={{mr: 0.5}}/> Text</ToggleButton>
                </ToggleButtonGroup>
                {loadingData && <Typography variant="caption" color="text.secondary">Loading items...</Typography>}
            </Stack>

            {fetchError && <Alert severity="warning" sx={{mb: 1.5}}>{fetchError}</Alert>}
            {switchError && <Alert severity="warning" sx={{mb: 1.5}}>{switchError}</Alert>}

            {mode === 'visual' && renderVisual()}

            {mode === 'tree' && (
                <Box sx={{'& .jer-editor-container': {fontFamily: 'monospace', fontSize: '0.875rem'}}}>
                    <JsonEditReact
                        data={value} setData={onChange} rootName="criteria" collapse={2}
                        restrictEdit={disabled} restrictDelete={disabled} restrictAdd={disabled}
                        restrictTypeSelection={disabled} minWidth="100%"
                    />
                </Box>
            )}

            {mode === 'text' && (
                <>
                    {textError && <Alert severity="error" sx={{mb: 1}}>{textError}</Alert>}
                    <TextField
                        fullWidth multiline minRows={4} maxRows={20}
                        value={textValue} onChange={handleTextChange} disabled={disabled} error={!!textError}
                        placeholder='[{"action":"game_play","entity":"machine_category","criteria":{"amount":3}}]'
                        slotProps={{input: {sx: {fontFamily: 'monospace', fontSize: '0.875rem'}}}}
                    />
                </>
            )}
        </Box>
    );
}

ChallengeCriteriaEditor.validate = (value) => {
    if (!Array.isArray(value)) return 'Must be an array';
    if (value.length === 0) return 'At least one criteria entry is required';
    for (let i = 0; i < value.length; i++) {
        const errors = validateRow(value[i]);
        if (Object.keys(errors).length > 0) {
            return `Row ${i + 1}: ${Object.values(errors).join(', ')}`;
        }
    }
    return null;
};

export default ChallengeCriteriaEditor;
